import { schedule } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import { task, timeout } from 'ember-concurrency';
import type { NamedArgs, PositionalArgs } from 'ember-modifier';
import Modifier from 'ember-modifier';

import styles from './highlight-element.module.css';

const ANIMATION_DURATION_IN_MILLISECONDS = 4000;

interface HighlightElementSignature {
  Args: {
    Named: {
      highlightParentElement?: boolean;
      id: string | undefined;
      isLoading?: boolean;
    };
    Positional: [];
  };
  Element: Element;
}

export default class HighlightElementModifier extends Modifier<HighlightElementSignature> {
  @service declare elementHighlighter: Services['element-highlighter'];
  @service declare router: Services['router'];

  removeHighlightTask = task(
    { drop: true },
    async (element: Element): Promise<void> => {
      if (macroCondition(isTesting())) {
        return;
      }

      await timeout(ANIMATION_DURATION_IN_MILLISECONDS);

      element.classList.remove(styles['highlight']);
    },
  );

  get targetId(): string | undefined {
    const queryParams = this.router.currentRoute?.queryParams;

    return queryParams?.['highlight'] as string | undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  modify(
    element: Element,
    _positional: PositionalArgs<HighlightElementSignature>,
    named: NamedArgs<HighlightElementSignature>,
  ) {
    const { id, highlightParentElement = false, isLoading = false } = named;
    const { targetId } = this;

    if (id === undefined || targetId === undefined) {
      return;
    }

    if (isLoading) {
      return;
    }

    if (id !== targetId) {
      return;
    }

    if (this.elementHighlighter.hasBeenHighlighted(id)) {
      return;
    }

    this.elementHighlighter.markAsHighlighted(id);

    const targetElement =
      highlightParentElement && element.parentElement
        ? element.parentElement
        : element;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const applyHighlight = () => {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      targetElement.classList.add(styles['highlight']);
      void this.removeHighlightTask.perform(targetElement);
    };

    // Defer scroll + class-add until layout is stable. vertical-collection (used
    // by ember-table) measures its scroll container in the first rAF after
    // didInsertElement. Waiting for afterRender + one rAF ensures that
    // measurement has run before we call scrollIntoView.
    let pendingAnimationFrameId: number | undefined;

    // eslint-disable-next-line ember/no-runloop
    schedule('afterRender', () => {
      if (macroCondition(isTesting())) {
        // Tests don't track rAF via settled(), so apply directly to keep assertions synchronous.
        applyHighlight();
        targetElement.classList.add(styles['is-testing']);
      } else {
        pendingAnimationFrameId = requestAnimationFrame(applyHighlight);
      }
    });

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (pendingAnimationFrameId !== undefined) {
        cancelAnimationFrame(pendingAnimationFrameId);
      }

      void this.removeHighlightTask.cancelAll();

      targetElement.classList.remove(styles['highlight']);
      targetElement.classList.remove(styles['is-testing']);
    };
  }
}
